import { useEffect, useRef } from "react"
import { getCachedBlobUrl, ImageCacheType, useCachedImage } from "./useCachedImage"

// Note the image will only be fetched if returned containerRef is assigned to an element and that element is in view
export function useLazyLoadedImage({
    type,
    imageKey,
    fetchBlob,
    dependencies = [],
}: {
    type: ImageCacheType
    imageKey: string
    fetchBlob: () => Promise<Blob | undefined>
    dependencies?: unknown[]
}) {
    const containerRef = useRef<HTMLDivElement>(null)
    const { blobUrl, fetchBlobUrl, isFetching, isError } = useCachedImage({
        type,
        imageKey,
        fetchBlob,
    })
    useEffect(() => {
        const element = containerRef.current
        if (!element) return
        if (getCachedBlobUrl(type, imageKey)) return
        const observer = new IntersectionObserver(async (entries) => {
            const entry = entries[0]
            if (!entry.isIntersecting) return
            observer.unobserve(element)
            fetchBlobUrl()
        })
        observer.observe(element)
        return () => observer.disconnect()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchBlobUrl, ...dependencies])
    return {
        blobUrl,
        isFetching,
        isError,
        containerRef,
    }
}
